import { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  compareIsoDate,
  isBetweenIso,
  monthCells,
  parseIsoDate,
  startOfMonth,
  toIsoDate,
  WEEKDAYS,
} from "./date-calendar";

export interface DateCalendarGridProps {
  month: Date;
  onMonthChange: (month: Date) => void;
  /** Highlight a single day (single-date mode). */
  selected?: string;
  /** Inclusive range highlight (range mode). */
  rangeFrom?: string;
  rangeTo?: string;
  /** Preview end while selecting (inclusive). */
  rangeHover?: string;
  onDayClick: (iso: string) => void;
  onDayHover?: (iso: string) => void;
  className?: string;
}

export function DateCalendarGrid({
  month,
  onMonthChange,
  selected,
  rangeFrom,
  rangeTo,
  rangeHover,
  onDayClick,
  onDayHover,
  className,
}: DateCalendarGridProps) {
  const todayIso = toIsoDate(new Date());
  const cells = useMemo(() => monthCells(month), [month]);
  const monthLabel = month.toLocaleDateString(undefined, {
    month: "long",
    year: "numeric",
  });

  const rangeEnd =
    rangeFrom && (rangeTo || rangeHover)
      ? compareIsoDate(rangeFrom, rangeTo || rangeHover!) <= 0
        ? rangeTo || rangeHover
        : rangeTo || rangeHover
      : undefined;
  const rangeStart =
    rangeFrom && rangeEnd
      ? compareIsoDate(rangeFrom, rangeEnd) <= 0
        ? rangeFrom
        : rangeEnd
      : rangeFrom;
  const rangeEndNorm =
    rangeFrom && rangeEnd
      ? compareIsoDate(rangeFrom, rangeEnd) <= 0
        ? rangeEnd
        : rangeFrom
      : undefined;

  return (
    <div className={className}>
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          aria-label="Previous month"
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/60 hover:text-foreground"
          onClick={() =>
            onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))
          }
        >
          <ChevronLeft className="size-4" />
        </button>
        <span className="text-sm font-semibold">{monthLabel}</span>
        <button
          type="button"
          aria-label="Next month"
          className="flex size-7 items-center justify-center rounded-md text-muted-foreground hover:bg-accent/60 hover:text-foreground"
          onClick={() =>
            onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))
          }
        >
          <ChevronRight className="size-4" />
        </button>
      </div>

      <div className="mt-2 grid grid-cols-7 gap-px">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="pb-1 text-center text-[10px] font-medium text-muted-foreground"
          >
            {day}
          </div>
        ))}
        {cells.map((cell) => {
          const isSelected = selected === cell.iso;
          const isToday = cell.iso === todayIso;
          const inRange =
            rangeStart &&
            rangeEndNorm &&
            isBetweenIso(cell.iso, rangeStart, rangeEndNorm);
          const isRangeEdge =
            cell.iso === rangeStart || cell.iso === rangeEndNorm;
          return (
            <button
              key={cell.iso}
              type="button"
              onClick={() => onDayClick(cell.iso)}
              onMouseEnter={() => onDayHover?.(cell.iso)}
              className={cn(
                "flex size-8 items-center justify-center rounded-md text-xs",
                !cell.inMonth && "text-muted-foreground/40",
                cell.inMonth && "hover:bg-accent/60",
                isToday && !isSelected && !inRange && "font-semibold text-primary",
                inRange && !isRangeEdge && "rounded-none bg-primary/15",
                isRangeEdge && "bg-primary text-primary-foreground hover:bg-primary",
                isSelected &&
                  !inRange &&
                  "bg-primary text-primary-foreground hover:bg-primary",
              )}
            >
              {Number(cell.iso.slice(8, 10))}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/** Pick a month to show when opening the calendar. */
export function initialCalendarMonth(
  ...isos: (string | undefined)[]
): Date {
  for (const iso of isos) {
    const d = iso ? parseIsoDate(iso) : null;
    if (d) return startOfMonth(d);
  }
  return startOfMonth(new Date());
}
